import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { AdditionalStepsService } from '../../../../../../core/services/additional-steps.service';
import { TemplateService } from '../../../../../../core/lists/_services/template.service';
import { TaskActionsService } from '../../../../../../core/services/task-actions.service';
import { ActionService } from '../../../../../../core/lists/_services/action.service';
import { CustomMappingEditorComponent } from './custom-mapping-editor/custom-mapping-editor.component';
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kt-task-actions-tab',
  templateUrl: './task-actions-tab.component.html',
  styleUrls: ['./task-actions-tab.component.scss'],
})
export class TaskActionsTabComponent implements OnInit, OnDestroy {
  form: FormGroup;
  index = '';
  @Input() additionalSteps = [];
  @Input() canSetAdditionalSteps;
  actionAutocompleteCall = this.actionsService.actionsAutocomplete.bind(this.actionsService);
  @ViewChild('actionAutocomplete') actionAutocomplete: GeneralAutocompleteComponent;
  mappedActions;
  mapper: any;
  mainTemplateId = undefined;
  @Input() mode;
  updateTaskActionsMode = false;
  taskActionUpdating: any;
  mappingError = false;
  private subscriptions: Subscription[] = [];
  mapperHasValue = false;
  @Input() set templateId(value: number) {
    if (!value) {
      return;
    }
    this.mainTemplateId = value;
  }
  @Input() lockedByAdmin;

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private actionsService: ActionService,
    private additionalStepsService: AdditionalStepsService,
    private taskActionsService: TaskActionsService,
    private templateService: TemplateService,
    private layoutUtils: LayoutUtilsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      additional_step: [undefined, Validators.required],
      action: [undefined, Validators.required],
    });
    if (this.lockedByAdmin) this.mode = 'view';
    // View Mode
    if (this.mode === 'view') this.form.disable();

    const reloadSubscription = this.taskActionsService.reloadTaskActions.subscribe(() => {
      this.loadTaskActions(this.mainTemplateId);
    });
    this.subscriptions.push(reloadSubscription);
  }

  reset(): void {
    this.index = '';
    this.updateTaskActionsMode = false;
    this.form.reset();
    this.actionAutocomplete?.setValue(null);
    this.actionAutocomplete.setErrors(null);
    const nameFormControl = this.form.get('additional_step') as FormControl;
    nameFormControl.setErrors(null);
    this.mapper = [];
    this.mappingError = false;
  }

  hasError(controlName: string, errorType: 'required' | 'duplicate'): boolean {
    const control = this.form.get(controlName);
    return control.touched && control.hasError(errorType);
  }

  getErrorMessage(controlLabel: string, errorType: 'required' | 'duplicate'): string {
    switch (errorType) {
      case 'required':
        return `${controlLabel} field is required`;
      case 'duplicate':
        return `${controlLabel} field must be unique`;
    }
  }

  addTaskAction(event: Event): void {
    event.stopPropagation();
    this.mappingError = false;
    const controls = this.form.controls;
    this.actionAutocomplete.updateValueAndValidity();
    if (this.form.invalid || !this.mapper || this.mapper.length === 0) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.mappingError = true;
      return;
    }
    const body = {
      id: undefined,
      action_id: this.form.controls.action.value?.id,
      additional_step_template_id: this.form.controls.additional_step.value?.id,
      mapper: this.mapper,
    };
    if (this.updateTaskActionsMode) {
      body.id = this.taskActionUpdating?.id;
    }
    this.taskActionsService.postPutActionAdditionalStep(body).subscribe(() => {
      this.loadTaskActions(this.mainTemplateId);
      if (this.updateTaskActionsMode) {
        this.layoutUtils.showActionNotification(
          'Task Action updated successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
        this.reset();
        this.updateTaskActionsMode = false;
        return;
      }
      this.layoutUtils.showActionNotification(
        'Task Action added successfully',
        MessageType.Create,
        3000,
        false,
        false,
      );
    });
    this.reset();
  }

  deleteTaskAction(id: number): void {
    const dialogRef = this.layoutUtils.confirmAction(
      'Delete Task Action',
      'Are you sure you want to delete this task action?',
      '',
    );
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.taskActionsService.deleteActionAdditionalStep(id).subscribe(() => {
        this.loadTaskActions(this.mainTemplateId);
        this.reset();
        this.layoutUtils.showActionNotification(
          'Task Action deleted successfully',
          MessageType.Delete,
          3000,
          false,
          false,
        );
      });
    });
  }

  filterActionFn(action: any): string {
    return action ? action.name : '';
  }

  onActionChange(val) {
    const currentAction = this.form.get('action')?.value;

    this.form.get('action')?.setValue(val ?? null);
    this.form.get('action')?.updateValueAndValidity();

    this.mapperHasValue = false;
    this.mapper = [];

    const currentId = currentAction?.id ?? null;
    const newId = val?.id ?? null;

    const isSameId = currentId === newId;

    if (!isSameId) {
      this.updateTaskActionsMode = false;
    }
  }

  goMapping() {
    const controls = this.form.controls;
    this.actionAutocomplete.updateValueAndValidity();
    if (this.form.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }
    const action = this.form.controls.action.value || null;
    const additional_step = this.form.controls.additional_step.value || null;

    if (!action || !additional_step) {
      return;
    }
    const dialogRef = this.dialog.open(CustomMappingEditorComponent, {
      height: '70vh',
      width: '70vw',
      data: {
        mode: this.updateTaskActionsMode ? 'update' : 'create',
        action,
        additional_step,
        mapper: this.mapper,
        mapperHasValue: this.mapperHasValue,
        templateID: this.mainTemplateId,
      },
    });
    dialogRef.afterClosed().subscribe((mapper) => {
      if (mapper) {
        this.mappingError = false;
        this.mapper = mapper;
        this.cdr.detectChanges();
        this.mapperHasValue = mapper.submittedValues;
      }
    });
  }

  updateTaskAction(taskAction) {
    this.index = `${taskAction.id}`;
    this.taskActionUpdating = taskAction;
    this.updateTaskActionsMode = true;
    this.form.get('action').setValue(taskAction.action);
    this.actionAutocomplete?.setValue(taskAction.action);
    const editTemplate = this.additionalSteps.filter(
      (e) => e.id === taskAction.additional_step_template.id,
    );
    this.form.get('additional_step').setValue(editTemplate[0]);
    this.mapper = taskAction.mapper;
    this.mappingError = false;
    this.cdr.detectChanges();
  }

  loadTaskActions(templateID: number) {
    if (templateID)
      this.taskActionsService.getActionAdditionalStep(templateID).subscribe((actions) => {
        this.mappedActions = actions?.data || [];
        this.cdr.detectChanges();
      });
  }

  cancelUpdateAction(event: Event): void {
    event.stopPropagation();

    this.reset();
  }
}
